import * as R from 'ramda'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'
import * as ReactRedux from 'react-redux'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'

import * as Herz from '@rushplay/herz'
import * as I18n from '@rushplay/i18n'

import * as Constants from './constants'
import * as Configuration from './configuration'
import * as Player from './player'
import * as ServerConfiguration from './server-configuration'
import * as Session from './combined-selectors'

export function ChatProvider({ children, ...props }) {
  const i18n = I18n.useI18n()

  const {
    liveChatDepartment,
    liveChatKey,
    locale,
  } = ServerConfiguration.useContext()

  const subBrand = ReactRedux.useSelector(state =>
    Configuration.getSubBrand(state.configuration)
  )

  // BE can't serve different config based on subbrand right now
  // liveChatKey for playboom24 needs to be hardcoded
  const chatKey =
    subBrand === Constants.Brands.PLAYBOOM24
      ? 'e42e0e02-c739-43f7-94fc-ab05234db0fa'
      : liveChatKey

  const player = ReactRedux.useSelector(state => ({
    birthdate: Player.getBirthdate(state.player),
    valueSegment: Player.getValueSegment(state.player),
    name: Player.getDisplayName(state.player),
    username: Player.getUsername(state.player),
    email: Player.getEmail(state.player),
  }))

  const data = React.useMemo(
    () => ({
      birthDate: player.birthDate ? `DOB: ${player.birthdate}` : null,
      departmentId: R.pathOr(null, ['primary'], liveChatDepartment),
      fallbackDepartmentId: R.pathOr(null, ['fallback'], liveChatDepartment),
      name: player.name ? `${player.name} (${player.username})` : '',
      tags: R.filter(item => !R.isNil(item), [
        player.birthdate ? `DOB: ${player.birthdate}` : null,
        player.valueSegment ? `Value segment: ${player.valueSegment}` : null,
      ]),
    }),
    [
      player.name,
      player.username,
      player.birthdate,
      player.valueSegment,
      liveChatDepartment,
    ]
  )

  const translations = React.useMemo(
    () => ({
      'chat.concierge.avatarPath': i18n.translate('chat.concierge.avatar'),
      'chat.concierge.name': i18n.translate('chat.concierge.name'),
      'chat.concierge.title': i18n.translate('chat.concierge.title'),
      'chat.offlineForm.greeting': i18n.translate('chat.offline-form.greeting'),
      'chat.title': i18n.translate('chat.title'),
      'launcher.chatLabel': i18n.translate('chat.launcher.chat-label'),
      'launcher.label': i18n.translate('chat.launcher.label'),
    }),
    [
      i18n.translate('chat.concierge.avatar'),
      i18n.translate('chat.concierge.name'),
      i18n.translate('chat.concierge.title'),
      i18n.translate('chat.offline-form.greeting'),
      i18n.translate('chat.title'),
      i18n.translate('chat.launcher.chat-label'),
      i18n.translate('chat.launcher.label'),
    ]
  )

  if (!chatKey) {
    return null
  }

  return (
    <Herz.Chat.ChatProvider
      {...props}
      addScriptInHead
      appKey={chatKey}
      departmentId={data.departmentId}
      email={player.email}
      fallbackDepartmentId={data.fallbackDepartmentId}
      language={locale.language}
      name={data.name}
      tags={data.tags}
      hidden={props.hidden}
      translations={translations}
    >
      {children}
    </Herz.Chat.ChatProvider>
  )
}

ChatProvider.defaultProps = {
  hidden: true,
}

ChatProvider.propTypes = {
  children: PropTypes.node,
  hidden: PropTypes.bool,
}

export function Chat() {
  const authenticated = Session.useAuthentication()

  const [allowLoad, setAllowLoad] = React.useState(false)
  const context = Herz.Chat.useContext()
  const visible = context.showWidget
  const location = ReactRouter.useLocation()

  React.useEffect(() => {
    if (visible || authenticated) {
      setAllowLoad(true)
    }
  }, [visible, authenticated])

  if (!location.pathname.includes('/faq')) {
    return null
  }

  if (allowLoad) {
    return <ChatScripts />
  }

  return null
}

function ChatScripts() {
  const context = Herz.Chat.useContext()

  return (
    <Helmet>
      <script type="text/javascript">
        {`window.zESettings = ${JSON.stringify(context.initialSettings)}`}
      </script>
      <script {...context.zendeskScript()} />
    </Helmet>
  )
}
